import { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../lib/theme";
import { API, mediaUrl, policySlug } from "../lib/api";
import useSEO from "../lib/useSEO";
import { FileText, ArrowLeft, Printer } from "@phosphor-icons/react";

/**
 * Clean, view-only on-site reader for an admin-uploaded legal/policy document.
 * .docx files are converted server-side to semantic HTML and rendered as a
 * proper web page (with a Print button). PDFs render inline. Nothing downloads.
 */
export default function PolicyPage() {
  const { slug } = useParams();
  const { settings } = useTheme();

  const doc = useMemo(() => {
    const docs = Array.isArray(settings?.policy_documents) ? settings.policy_documents : [];
    return docs.find((d) => d && d.url && policySlug(d.name) === slug);
  }, [settings?.policy_documents, slug]);

  useSEO({
    title: doc ? `${doc.name} — Wisewit` : "Legal — Wisewit",
    description: doc ? `${doc.name} for Wisewit Solutions.` : "Wisewit legal documents.",
  });

  const [content, setContent] = useState({ loading: true, has_html: false, html: "" });

  const storagePath = (doc?.url || "").replace(/^\/api\/files\//, "");
  const isPdf = (() => {
    const u = (doc?.url || "").toLowerCase();
    const f = (doc?.filename || "").toLowerCase();
    return u.endsWith(".pdf") || f.endsWith(".pdf");
  })();

  useEffect(() => {
    let alive = true;
    if (!doc?.url) { setContent({ loading: false, has_html: false, html: "" }); return; }
    setContent((c) => ({ ...c, loading: true }));
    axios.get(`${API}/policy-content/${storagePath}`)
      .then(({ data }) => { if (alive) setContent({ loading: false, has_html: !!data.has_html, html: data.html || "" }); })
      .catch(() => { if (alive) setContent({ loading: false, has_html: false, html: "" }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storagePath]);

  const printDoc = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const updated = doc?.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "";
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${doc.name}</title>
      <style>body{font-family:Georgia,'Times New Roman',serif;max-width:720px;margin:48px auto;padding:0 28px;line-height:1.75;color:#111}
      h1{font-family:system-ui,Arial,sans-serif;font-size:28px;margin:0 0 4px} .meta{color:#666;font:12px system-ui;margin-bottom:28px;text-transform:uppercase;letter-spacing:.12em}
      h2,h3{font-family:system-ui,Arial,sans-serif} table{border-collapse:collapse;width:100%} td,th{border:1px solid #ccc;padding:6px 10px} a{color:#0a58ca}</style></head>
      <body><h1>${doc.name}</h1>${updated ? `<div class="meta">Last updated · ${updated}</div>` : ""}${content.html}
      <script>window.onload=function(){window.focus();window.print();}</script></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="policy-page" className="px-6 md:px-14 py-16 md:py-24 min-h-[70vh]">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] hover:text-white transition-colors" data-testid="policy-back">
          <ArrowLeft size={12} /> Back to wisewit.ai
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <FileText size={24} color="#00E5FF" weight="duotone" />
          <div className="text-overline">◢ Legal</div>
        </div>
        <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl sm:text-5xl" style={{ color: "var(--ww-heading)" }}>
          {doc ? doc.name : "Document not found"}
        </h1>

        {doc?.uploaded_at && (
          <div className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]" data-testid="policy-updated">
            Last updated ·{" "}
            {new Date(doc.uploaded_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </div>
        )}

        {!doc ? (
          <div className="mt-8 ww-card p-10 text-center">
            <p className="text-[#8E8E93]">
              This policy hasn&apos;t been published yet. Please check the footer for the
              documents that are currently available.
            </p>
            <Link to="/" className="btn-primary mt-6 inline-flex">Return home</Link>
          </div>
        ) : content.loading ? (
          <div className="mt-8 ww-card p-10 text-[#8E8E93]" data-testid="policy-loading">Loading document…</div>
        ) : content.has_html ? (
          <>
            <div className="mt-6">
              <button onClick={printDoc} className="btn-secondary" data-testid="policy-print">
                <Printer size={14} /> Print
              </button>
            </div>
            <article
              className="mt-8 ww-card p-8 md:p-12 policy-prose"
              data-testid="policy-html"
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          </>
        ) : isPdf ? (
          <div className="mt-8 border border-[#2A2A2A] bg-[#0a0a0a]" data-testid="policy-viewer">
            <object data={mediaUrl(doc.url)} type="application/pdf" className="w-full" style={{ height: "80vh" }}>
              <iframe title={doc.name} src={mediaUrl(doc.url)} className="w-full" style={{ height: "80vh", border: 0 }} />
            </object>
          </div>
        ) : (
          <div className="mt-8 ww-card p-10 text-center" data-testid="policy-unavailable">
            <FileText size={40} color="#8E8E93" weight="duotone" className="mx-auto" />
            <p className="mt-4 text-[#8E8E93]">
              This document couldn&apos;t be rendered for on-site viewing. Please re-upload
              it as a .docx or .pdf from the admin.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
